import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Palette, Pipette, Copy, Check } from 'lucide-react';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  onClose?: () => void;
  showPresets?: boolean;
  className?: string;
}

interface HSL {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

interface RGB {
  r: number; // 0-255
  g: number; // 0-255
  b: number; // 0-255
}

// Color conversion utilities
const hexToRgb = (hex: string): RGB | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const rgbToHsl = (r: number, g: number, b: number): HSL => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToRgb = (h: number, s: number, l: number): RGB => {
  h /= 360;
  s /= 100;
  l /= 100;
  
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
};

// Beautiful color presets inspired by modern design tools
const COLOR_PRESETS = [
  // Neutrals
  '#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6', '#ced4da', '#adb5bd', '#6c757d', '#495057', '#343a40', '#212529',
  // Blues
  '#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6', '#42a5f5', '#2196f3', '#1e88e5', '#1976d2', '#1565c0', '#0d47a1',
  // Greens
  '#e8f5e8', '#c8e6c9', '#a5d6a7', '#81c784', '#66bb6a', '#4caf50', '#43a047', '#388e3c', '#2e7d32', '#1b5e20',
  // Reds
  '#ffebee', '#ffcdd2', '#ef9a9a', '#e57373', '#ef5350', '#f44336', '#e53935', '#d32f2f', '#c62828', '#b71c1c',
  // Purples
  '#f3e5f5', '#e1bee7', '#ce93d8', '#ba68c8', '#ab47bc', '#9c27b0', '#8e24aa', '#7b1fa2', '#6a1b9a', '#4a148c',
  // Oranges
  '#fff3e0', '#ffe0b2', '#ffcc02', '#ffb74d', '#ffa726', '#ff9800', '#fb8c00', '#f57c00', '#ef6c00', '#e65100',
];

export const AdvancedColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onChange,
  onClose,
  showPresets = true,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState<'picker' | 'presets'>('picker');
  const [inputValue, setInputValue] = useState(color);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hueRef = useRef<HTMLCanvasElement>(null);
  
  // Convert current color to HSL for picker
  const rgb = hexToRgb(color);
  const hsl = rgb ? rgbToHsl(rgb.r, rgb.g, rgb.b) : { h: 0, s: 0, l: 50 };
  
  const [currentHsl, setCurrentHsl] = useState<HSL>(hsl);

  // Update HSL when color prop changes
  useEffect(() => {
    const rgb = hexToRgb(color);
    if (rgb) {
      const newHsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
      setCurrentHsl(newHsl);
      setInputValue(color);
    }
  }, [color]);

  // Draw color picker canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const width = canvas.width;
    const height = canvas.height;
    
    // Create saturation/lightness gradient
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const s = (x / width) * 100;
        const l = ((height - y) / height) * 100;
        const rgb = hslToRgb(currentHsl.h, s, l);
        
        const index = (y * width + x) * 4;
        data[index] = rgb.r;
        data[index + 1] = rgb.g;
        data[index + 2] = rgb.b;
        data[index + 3] = 255;
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  }, [currentHsl.h]);

  // Draw hue slider
  useEffect(() => {
    const canvas = hueRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const width = canvas.width;
    const height = canvas.height;
    
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    for (let i = 0; i <= 360; i += 60) {
      const rgb = hslToRgb(i, 100, 50);
      gradient.addColorStop(i / 360, `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
    }
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }, []);

  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.stopPropagation(); // Prevent modal from closing
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const s = (x / canvas.width) * 100;
    const l = ((canvas.height - y) / canvas.height) * 100;

    const newHsl = { ...currentHsl, s, l };
    setCurrentHsl(newHsl);

    const rgb = hslToRgb(newHsl.h, newHsl.s, newHsl.l);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    onChange(hex);
  }, [currentHsl, onChange]);

  const handleHueClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.stopPropagation(); // Prevent modal from closing
    const canvas = hueRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const h = (x / canvas.width) * 360;

    const newHsl = { ...currentHsl, h };
    setCurrentHsl(newHsl);

    const rgb = hslToRgb(newHsl.h, newHsl.s, newHsl.l);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    onChange(hex);
  }, [currentHsl, onChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      onChange(value);
    }
  }, [onChange]);

  const handlePresetClick = useCallback((presetColor: string) => {
    onChange(presetColor);
  }, [onChange]);

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(color);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy color:', err);
    }
  }, [color]);

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Palette className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Color Picker</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex mb-4 bg-gray-100 rounded-lg p-1">
        <button
          onClick={(e) => { e.stopPropagation(); setActiveTab('picker'); }}
          className={`flex-1 py-2 px-3 text-sm rounded-md transition-colors ${
            activeTab === 'picker'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Custom
        </button>
        {showPresets && (
          <button
            onClick={(e) => { e.stopPropagation(); setActiveTab('presets'); }}
            className={`flex-1 py-2 px-3 text-sm rounded-md transition-colors ${
              activeTab === 'presets'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Presets
          </button>
        )}
      </div>

      {activeTab === 'picker' ? (
        <div className="space-y-4">
          {/* Color Canvas */}
          <div className="relative">
            <canvas
              ref={canvasRef}
              width={256}
              height={256}
              className="w-full h-48 border border-gray-200 rounded cursor-crosshair"
              onClick={handleCanvasClick}
            />
            {/* Picker indicator */}
            <div
              className="absolute w-3 h-3 border-2 border-white rounded-full shadow-md pointer-events-none transform -translate-x-1/2 -translate-y-1/2"
              style={{
                left: `${(currentHsl.s / 100) * 100}%`,
                top: `${100 - (currentHsl.l / 100) * 100}%`,
              }}
            />
          </div>

          {/* Hue Slider */}
          <div className="relative">
            <canvas
              ref={hueRef}
              width={256}
              height={20}
              className="w-full h-5 border border-gray-200 rounded cursor-pointer"
              onClick={handleHueClick}
            />
            {/* Hue indicator */}
            <div
              className="absolute w-3 h-7 border-2 border-white rounded shadow-md pointer-events-none transform -translate-x-1/2 -translate-y-1"
              style={{ left: `${(currentHsl.h / 360) * 100}%` }}
            />
          </div>

          {/* Color Input */}
          <div className="flex items-center space-x-2">
            <div
              className="w-10 h-10 border border-gray-200 rounded"
              style={{ backgroundColor: color }}
            />
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm font-mono"
              placeholder="#000000"
            />
            <button
              onClick={(e) => { e.stopPropagation(); copyToClipboard(); }}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Copy color"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-10 gap-1">
          {COLOR_PRESETS.map((presetColor, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); handlePresetClick(presetColor); }}
              className={`w-6 h-6 rounded border-2 transition-all hover:scale-110 ${
                color === presetColor ? 'border-blue-500 shadow-md' : 'border-gray-200'
              }`}
              style={{ backgroundColor: presetColor }}
              title={presetColor}
            />
          ))}
        </div>
      )}
    </div>
  );
};
